import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App.tsx";
import "./index.css";
import "./styles/clock-fonts.css";
import "./styles/performance.css";

// Defer Sentry initialization to after first paint for better startup performance
setTimeout(() => {
  Sentry.init({
    dsn: "https://3ab97d513fdcd80a2d20b49d65cb66bc@o4510314729832448.ingest.us.sentry.io/4510314731012096",
    
    // Environment tracking
    environment: import.meta.env.MODE,
    
    // PII and user context
    sendDefaultPii: true,
    
    // Enable structured logging
    enableLogs: true,
    
    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // Integrations
    integrations: [
      // Capture console errors and warnings automatically
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
      // React-specific error boundary and profiling
      Sentry.browserTracingIntegration(),
    ],
  });
}, 0);

// Euclid Engine Version Stamp - Forces rebuild
console.log('%c🔷 EUCLID v2.1 INITIALIZING', 'color: #60a5fa; font-size: 14px; font-weight: bold');
console.log('%cEuclid Super Intelligence Engine - Build: ' + new Date().toISOString(), 'color: #a78bfa; font-size: 12px');

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Wrapper component to ensure proper React context
const AppWrapper = () => {
  const isValidClerkKey = PUBLISHABLE_KEY && PUBLISHABLE_KEY !== "YOUR_PUBLISHABLE_KEY" && PUBLISHABLE_KEY.startsWith("pk_");
  
  if (!isValidClerkKey) {
    // When no valid Clerk key, render app without ClerkProvider
    return <App />;
  }

  // When valid Clerk key exists, wrap with ClerkProvider
  return (
    <ClerkProvider 
      publishableKey={PUBLISHABLE_KEY} 
      afterSignOutUrl="/"
      appearance={{
        variables: {
          colorPrimary: '#f97316',
          colorBackground: '#0f172a',
          colorInputBackground: '#1e293b',
          colorInputText: '#f8fafc',
          colorText: '#f8fafc',
          colorTextSecondary: '#94a3b8',
          colorTextOnPrimaryBackground: '#f8fafc',
          colorNeutral: '#64748b',
          colorDanger: '#ef4444',
          colorSuccess: '#22c55e',
          colorWarning: '#f59e0b',
          borderRadius: '8px'
        },
        elements: {
          formButtonPrimary: {
            background: '#f97316',
            color: '#ffffff',
            '&:hover': {
              background: '#ea580c'
            }
          },
          card: {
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
            color: '#f8fafc'
          },
          organizationSwitcher: {
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px'
          },
          organizationSwitcherTrigger: {
            background: 'transparent',
            color: '#f8fafc',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)'
            }
          },
          modalContent: {
            background: '#0f172a',
            color: '#f8fafc'
          },
          modalCloseButton: {
            color: '#f8fafc'
          },
          formFieldLabel: {
            color: '#f8fafc'
          },
          formFieldInput: {
            background: '#1e293b',
            border: '1px solid #334155',
            color: '#f8fafc'
          }
        }
      }}
    >
      <App />
    </ClerkProvider>
  );
};

// Render with proper React context
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
);
